import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { INgsiAIS } from '../../lib/ngsild/interfaces/ngsi-ais.interface';
import { SharedModule } from '../../../shared/shared.module'

const comparator = (prev: INgsiAIS, curr: INgsiAIS) => prev.id === curr.id;

@Component({
    selector: 'lib-map-sidepanel-content',
    imports: [SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './map-sidepanel-content.component.html',
    styleUrl: './map-sidepanel-content.component.scss'
})
export class MapSidepanelContentComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>()
  @Input() widgetId: string | undefined

  private _vesselItems: INgsiAIS[] = [];
  @Input() set vesselItems(value: INgsiAIS[] | null) {
    this._vesselItems = value || [];  // Ensure it is always an array
  }
  get vesselItems(): INgsiAIS[] {
    return this._vesselItems;
  }

  @Output() selectedVesselItem = new EventEmitter<INgsiAIS>()

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('sidePanel', { static: false }) sidePanel!: ElementRef;

  maxHeightPercentage: number = 75; // Maximum height as a percentage
  baseHeightPercentage: number = 26; // Height for 1 row
  incrementHeightPercentage: number = 5.10; // Increment for each additional row
  currentHeightPercentage: number = 0;

  // table configuration
  dataSource: MatTableDataSource<any>;
  columns = [
    {
      columnDef: 'senderMmsi',
      header: 'MMSI',
      cell: (element: any) => `${element?.senderMmsi || ''}`,
    },
    {
      columnDef: 'name',
      header: 'Name',
      cell: (element: any) => `${element?.name || ''}`,
    },
    {
      columnDef: 'cog',
      header: 'COG',
      cell: (element: any) => `${element?.cog + ' °' || ''}`,
    },
    {
      columnDef: 'sog',
      header: 'SOG',
      cell: (element: any) => `${element?.sog + ' kn' || ''} `,
    },
  ];

  displayedColumns = ['selected_vessel_item', ...this.columns.map(
    column => column.columnDef)
  ];

  vesselItemSelection = new SelectionModel<INgsiAIS>(false, [], true, comparator);
  searchFilterValue: string = '';

  constructor(private readonly renderer: Renderer2) {
    this.dataSource = new MatTableDataSource<any>([]);
  }

  ngAfterViewInit(): void {
    this.dataSource = new MatTableDataSource(this.vesselItems);
    this.dataSource.sort = this.sort;

    if (this.sidePanel) {
      this.adjustSidePanelHeight();
    }

    this.vesselItemSelection.changed
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectionChange: SelectionChange<INgsiAIS>) => {
        const entity = selectionChange.added[0] as INgsiAIS | undefined;

        this.selectedVesselItem.emit(entity);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vesselItems']) {
      if (!changes['vesselItems'].firstChange) {
        this.updateDataSource();
      }

      if (this.sidePanel) {
        this.adjustSidePanelHeight();
      }
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe to all component subscriptions
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete()
  }

  private updateDataSource(): void {
    this.dataSource.data = this.vesselItems;
  }

  private adjustSidePanelHeight(): void {
    const rowCount = this.getTableRowCount();
    const panelHeight = this.calculatePanelHeight(rowCount);

    if (this.currentHeightPercentage !== panelHeight) {
      this.currentHeightPercentage = panelHeight;
      this.applyHeightToSidePanel(`${panelHeight}%`);
    }
  }

  private getTableRowCount(): number {
    return this.dataSource.data.length;
  }

  private calculatePanelHeight(rowCount: number): number {
    if (rowCount <= 0) {
      // Default height when no rows exist
      return this.baseHeightPercentage;
    }

    // Calculate height dynamically for up to `maxHeightPercentage`
    const calculatedHeight = this.baseHeightPercentage + (rowCount - 1) * this.incrementHeightPercentage;

    // Cap the height at `maxHeightPercentage`
    return Math.min(calculatedHeight, this.maxHeightPercentage);
  }

  private applyHeightToSidePanel(height: string): void {
    this.renderer.setStyle(this.sidePanel.nativeElement, 'height', height);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  clearSearchFilter(): void {
    this.searchFilterValue = '';
    this.dataSource.filter = '';
  }

  getClassForValue(columnDef: string): string {
    if (columnDef === 'cog') {
      return 'is-cog-value';
    } else if (columnDef === 'sog') {
      return  'is-sog-value';
    } else {
      return '';
    }
  }

  /** The label for the checkbox on the passed row */
  onRowClicked(row?: INgsiAIS): string {
    if (!row) {
      return 'deselect';
    }

    return `${this.vesselItemSelection.isSelected(row) ? 'select' : 'deselect'}`;
  }

  // to manage the update and stable state for the checkbox
  trackById(index: number, row: INgsiAIS): string {
    return row.id;
  }
}
